// Third-party packages
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Container,
  Form,
  Header,
  Icon,
  Input,
  Grid,
  Menu,
  Message,
  Placeholder,
  Segment,
} from 'semantic-ui-react'
import dateFormat from 'dateformat'
import faker from '@faker-js/faker/dist/faker.js'
import { GenCC } from 'creditcard-generator'

// Main packages
import * as api from '../api'
import { AppContext } from './app_context'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

const CheckoutPage = () => {

  // Global app state
  const app = useContext(AppContext)
  const history = useHistory()

  const [shippingAddressStreet1, setShippingAddressStreet1] = useState('')
  const [shippingAddressStreet2, setShippingAddressStreet2] = useState('')
  const [shippingAddressCity, setShippingAddressCity] = useState('')
  const [shippingAddressState, setShippingAddressState] = useState('')
  const [shippingAddressPostalCode, setShippingAddressPostalCode] = useState('')
  const [shippingAddressCountry, setShippingAddressCountry] = useState('')
  const [billingAddressStreet1, setBillingAddressStreet1] = useState('')
  const [billingAddressStreet2, setBillingAddressStreet2] = useState('')
  const [billingAddressCity, setBillingAddressCity] = useState('')
  const [billingAddressState, setBillingAddressState] = useState('')
  const [billingAddressPostalCode, setBillingAddressPostalCode] = useState('')
  const [billingAddressCountry, setBillingAddressCountry] = useState('')
  const [billingCardName, setBillingCardName] = useState('')
  const [billingCardNumber, setBillingCardNumber] = useState('')
  const [billingCardExpiration, setBillingCardExpiration] = useState('')
  const [billingCardSecurityCode, setBillingCardSecurityCode] = useState('')
  const [billingCardPostalCode, setBillingCardPostalCode] = useState('')
  const [billingEmail, setBillingEmail] = useState('')
  const [paymentError, setPaymentError] = useState('')
  
  const randomUser = () => {
    faker.setLocale('en_US')
    const postalCode = faker.address.zipCode().split('-')[0]
    const user = {
      address: {
        street_1: faker.address.streetAddress(),
        street_2: Math.random() > 0.8 ? faker.address.secondaryAddress() : '',
        city: faker.address.cityName(),
        state: faker.address.stateAbbr(),
        postal_code: postalCode,
        country: 'US'
      },
      card: {
        name: `${faker.name.firstName()} ${faker.name.lastName()}`,
        number: GenCC('VISA')[0],
        expiration: dateFormat(faker.date.future(), 'mm/yy'),
        security_code: faker.finance.creditCardCVV(),
        postal_code: postalCode
      },
      email: faker.internet.email()
    }
    return user
  }
  const randomizeCheckoutForm = () => {
    const user = randomUser()
    setShippingAddressStreet1(user.address.street_1)
    setShippingAddressStreet2(user.address.street_2)
    setShippingAddressCity(user.address.city)
    setShippingAddressState(user.address.state)
    setShippingAddressPostalCode(user.address.postal_code)
    setShippingAddressCountry(user.address.country)
    setBillingAddressStreet1(user.address.street_1)
    setBillingAddressStreet2(user.address.street_2)
    setBillingAddressCity(user.address.city)
    setBillingAddressState(user.address.state)
    setBillingAddressPostalCode(user.address.postal_code)
    setBillingAddressCountry(user.address.country)
    setBillingCardName(user.card.name)
    setBillingCardNumber(user.card.number)
    setBillingCardExpiration(user.card.expiration)
    setBillingCardSecurityCode(user.card.security_code)
    setBillingCardPostalCode(user.card.postal_code)
    setBillingEmail(user.email)
  }
  
  useEffect(() => {
    randomizeCheckoutForm()
  }, [])

  var title = 'Checkout'
  var subtitle = ''

  const onSubmit = async (e) => {
    e.preventDefault()
    setPaymentError('') // Clear any previous errors
    
    const startTime = Date.now();
    
    // Start user action for checkout submission (critical action)
    if (window.faroTracker) {
      try {
        // Check if startUserAction exists before calling
        if (typeof window.faroTracker.startUserAction === 'function') {
          window.faroTracker.startUserAction('checkout-submit', {
            cart_value: app.cart.subtotal,
            cart_items: app.cart.size,
            items_count: app.cart.items?.length || 0,
            payment_method: 'card'
          }, {
            triggerName: 'form_submit',
            importance: 'critical' // Critical for alerting and journey grouping
          });
        }
        
        if (window.faroTracker.trackEvent) {
          window.faroTracker.trackEvent('payment_attempt_started', {
            cart_value: app.cart.subtotal,
            cart_items: app.cart.size,
            items_count: app.cart.items?.length || 0,
            form_completion_time: Date.now() - startTime
          });
        }
        
        if (window.faroTracker.trackConversionStep) {
          window.faroTracker.trackConversionStep('payment_submit', {
            cart_value: app.cart.subtotal,
            items_count: app.cart.items?.length || 0
          });
        }
      } catch (faroError) {
        // Don't let Faro errors block the checkout
        // Silently fail - tracking is optional
      }
    }
    
    // Get customer_id from app context or localStorage for order tracking
    const customerInfo = app.customerInfo || api.getCustomerInfo()
    const customerId = customerInfo?.id || null
    
    const data = {
      amount: app.cart.subtotal,
      customer_id: customerId,  // Include customer_id for order tracking
      shipping: {
        address: {
          street_1: shippingAddressStreet1,
          street_2: shippingAddressStreet2,
          city: shippingAddressCity,
          state: shippingAddressState,
          postal_code: shippingAddressPostalCode,
          country: shippingAddressCountry,
        }
      },
      billing: {
        address: {
          street_1: billingAddressStreet1 || shippingAddressStreet1,
          street_2: billingAddressStreet2 || shippingAddressStreet2,
          city: billingAddressCity || shippingAddressCity,
          state: billingAddressState || shippingAddressState,
          postal_code: billingAddressPostalCode || shippingAddressPostalCode,
          country: billingAddressCountry || shippingAddressCountry,
        },
        card: {
          name: billingCardName,
          number: billingCardNumber,
          expiration: billingCardExpiration,
          security_code: billingCardSecurityCode,
          postal_code: billingCardPostalCode,
        },
        name: billingCardName,
        email: billingEmail
      }
    }
    app.setLoading(true)
    try {
              const response = await api.checkout(data)
        await app.refreshCart(true) // Track cart refresh
        
        // Track payment completion time
        const paymentDuration = Date.now() - startTime;
        
        // Check both HTTP status and payment message to determine success
        const isSuccess = response.status == 200 && response.data?.message === 'success'
        
        if (isSuccess) {
          // Track successful payment (with error handling)
          try {
            if (window.faroTracker) {
              window.faroTracker.trackPaymentAttempt(data, 'success');
              window.faroTracker.trackEvent('payment_completed', {
                cart_value: app.cart.subtotal,
                items_count: app.cart.items?.length || 0,
                payment_duration: paymentDuration,
                success: true,
                redirect_to: 'confirmation'
              });
            }
          } catch (faroError) {
            console.warn('Faro tracking failed:', faroError);
          }
          history.push('/confirmation')
        } else {
        // Handle payment failures
        let errorMessage = 'Payment failed. Please try again.'
        
        // Debug: Log the response structure to help troubleshoot
        console.log('Payment error response:', {
          status: response.status,
          data: response.data
        });
        
        if (response.data) {
          // Extract error details from either root level or nested details
          const errorDetails = response.data.details || response.data
          const detailReason = errorDetails.reason  // The specific error reason (e.g., "Invalid card number")
          const failureType = errorDetails.failure_type
          const responseMessage = errorDetails.message || response.data.message
          
          // Handle validation failures from Lambda
          if (responseMessage === 'validation_failed') {
            const validationErrors = response.data.errors || []
            if (validationErrors.length > 0) {
              // Use the first validation error as the message
              errorMessage = validationErrors[0]
            } else {
              errorMessage = 'Payment validation failed. Please check your card details and try again.'
            }
          } else if (response.data.reason === 'payment_declined') {
            if (failureType === 'gateway_timeout') {
              errorMessage = 'Payment gateway timeout. Please try again in a moment.'
            } else if (failureType === 'insufficient_funds') {
              errorMessage = 'Payment declined: Insufficient funds.'
            } else if (detailReason === 'Invalid card number') {
              errorMessage = 'Invalid card number. Please check your card details.'
            } else if (responseMessage === 'fraud_blocked') {
              errorMessage = 'Payment blocked by fraud protection. Please try a different payment method or contact customer service.'
            } else {
              errorMessage = 'Payment was declined by your bank. Please check your card details or try a different payment method.'
            }
          } else if (response.data.reason === 'payment_service_error') {
            errorMessage = 'Payment service temporarily unavailable. Please try again later.'
          } else if (responseMessage && responseMessage !== 'success') {
            // Handle other failure messages
            errorMessage = `Payment failed: ${responseMessage}. Please try again.`
          }
        }
        
        setPaymentError(errorMessage)
      }
    } catch (error) {
      // Debug: Log the complete error structure
      console.log('Payment error caught:', {
        error: error,
        hasResponse: !!error.response,
        response: error.response,
        responseData: error.response?.data,
        errorMessage: error.message,
        errorCode: error.code,
        errorName: error.name,
        errorStack: error.stack?.substring(0, 200)
      });
      
      // Handle axios errors that might have response data
      if (error.response && error.response.data) {
        let errorMessage = 'Payment failed. Please try again.'
        const response = error.response
        
        // Try to parse the response data if it's a string
        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (parseError) {
            // Could not parse response as JSON, use original data
          }
        }
        
        if (responseData) {
          const errorDetails = responseData.details || responseData
          const detailReason = errorDetails.reason
          const failureType = errorDetails.failure_type
          const responseMessage = errorDetails.message
          
          if (responseData.reason === 'payment_declined') {
            // Check for fraud-related errors first (multiple ways fraud can be indicated)
            if (responseMessage === 'fraud_blocked' || 
                responseMessage === 'fraud_suspected' ||
                detailReason === 'fraud_blocked' ||
                detailReason === 'fraud_suspected' ||
                failureType === 'fraud_blocked' ||
                failureType === 'fraud_suspected') {
              errorMessage = 'Payment blocked by fraud protection. Please try a different payment method or contact customer service.'
            } else if (failureType === 'gateway_timeout') {
              errorMessage = 'Payment gateway timeout. Please try again in a moment.'
            } else if (failureType === 'insufficient_funds') {
              errorMessage = 'Payment declined: Insufficient funds.'
            } else if (detailReason === 'Invalid card number') {
              errorMessage = 'Invalid card number. Please check your card details.'
            } else {
              errorMessage = 'Payment was declined by your bank. Please check your card details or try a different payment method.'
            }
          } else if (responseData.reason === 'payment_service_error') {
            errorMessage = 'Payment service temporarily unavailable. Please try again later.'
          }
        }
        
        // Track detailed payment failure (with error handling)
        try {
          if (window.faroTracker) {
            const paymentError = new Error(errorMessage);
            window.faroTracker.trackPaymentAttempt(data, 'failure', paymentError);
            window.faroTracker.trackEvent('payment_failed_with_response', {
              cart_value: app.cart.subtotal,
              items_count: app.cart.items?.length || 0,
              payment_duration: Date.now() - startTime,
              error_message: errorMessage,
              error_type: window.faroTracker.categorizePaymentError?.(paymentError) || 'unknown',
              response_status: response.status,
              has_response_data: !!responseData,
              failure_source: 'axios_response_error'
            });
          }
        } catch (faroError) {
          console.warn('Faro tracking failed:', faroError);
        }
        
        setPaymentError(errorMessage)
      } else {
        // Track generic payment failure (with error handling)
        try {
          if (window.faroTracker) {
            const paymentError = new Error('Something went wrong during payment processing');
            window.faroTracker.trackPaymentAttempt(data, 'failure', paymentError);
            window.faroTracker.trackEvent('payment_failed_generic', {
              cart_value: app.cart.subtotal,
              items_count: app.cart.items?.length || 0,
              payment_duration: Date.now() - startTime,
              error_message: 'Generic processing error',
              error_type: 'network_error',
              failure_source: 'axios_no_response'
            });
          }
        } catch (faroError) {
          console.warn('Faro tracking failed:', faroError);
        }
        setPaymentError('Something went wrong during payment processing. Please try again.')
      }
    } finally {
      app.setLoading(false)
    }
  }

  var content = (<></>)
  if (app.cart.size === null) {
    ////  Loading Cart  ////////////////////////////////////////////////////////
    content = (
      <Placeholder>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    )

  } else if (app.cart.items.length === 0) {
    ////  Empty Cart  //////////////////////////////////////////////////////////
    subtitle = 'Your cart is empty'

  } else {
    ////  Checkout Form  ///////////////////////////////////////////////////////
    content = (
      <Segment.Group>
        <Segment color='teal' style={{ background: '#fcfcfc' }}>
          <Message icon color='yellow' style={{ marginBottom: '2em' }}>
            <Icon name='warning sign' />
            This store is only a simulation. Don't submit any real personal information.
          </Message>
          {paymentError && (
            <Message negative style={{ marginBottom: '2em' }}>
              <Message.Header>Payment Failed</Message.Header>
              <p>{paymentError}</p>
            </Message>
          )}
          <Header as='h4' content='Shipping Address' style={{ fontSize: '1.5em', margin: '.75em auto', textAlign: 'center' }} />
          <Form.Field
            control={Input}
            onChange={(e) => setShippingAddressStreet1(e.target.value)}
            placeholder='Street'
            value={shippingAddressStreet1}
          />
          <Form.Field
            control={Input}
            onChange={(e) => setShippingAddressStreet2(e.target.value)}
            placeholder='Street (2)'
            value={shippingAddressStreet2}
          />
          <Form.Group widths='equal'>
            <Form.Field
              control={Input}
              onChange={(e) => setShippingAddressCity(e.target.value)}
              placeholder='City'
              value={shippingAddressCity}
            />
            <Form.Field
              control={Input}
              onChange={(e) => setShippingAddressState(e.target.value)}
              placeholder='State'
              value={shippingAddressState}
            />
            <Form.Field
              control={Input}
              onChange={(e) => setShippingAddressPostalCode(e.target.value)}
              placeholder='Postal Code'
              value={shippingAddressPostalCode}
            />
            <Form.Field
              control={Input}
              onChange={(e) => setShippingAddressCountry(e.target.value)}
              placeholder='Country'
              value={shippingAddressCountry}
            />
          </Form.Group>
        </Segment>
        <Segment style={{ background: '#fcfcfc' }}>
          <Header as='h4' content='Payment Method' style={{ fontSize: '1.5em', margin: '.75em auto', textAlign: 'center' }} />
          <Form.Field
            control={Input}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder='Email Address'
            type='email'
            value={billingEmail}
            icon='mail'
            iconPosition='left'
          />
          <Form.Field
            control={Input}
            onChange={(e) => setBillingCardName(e.target.value)}
            placeholder='Name on Card'
            value={billingCardName}
          />
          <Form.Field
            control={Input}
            onChange={(e) => setBillingCardNumber(e.target.value)}
            placeholder='Card Number'
            value={billingCardNumber}
          />
          <Form.Group widths='equal'>
            <Form.Field
              control={Input}
              onChange={(e) => setBillingCardExpiration(e.target.value)}
              placeholder='Card Expiration'
              value={billingCardExpiration}
            />
            <Form.Field
              control={Input}
              onChange={(e) => setBillingCardSecurityCode(e.target.value)}
              placeholder='Card Security Code'
              value={billingCardSecurityCode}
            />
            <Form.Field
              control={Input}
              onChange={(e) => setBillingCardPostalCode(e.target.value)}
              placeholder='Card Postal Code'
              value={billingCardPostalCode}
            />
          </Form.Group>
        </Segment>
      </Segment.Group>
    )
  }

  return (<>
    <style type='text/css'>
      {`#root { padding-bottom: 8em }`}
    </style>
    <Form onSubmit={onSubmit}>
      <Segment style={{ margin: 0, padding: '4em 0' }} vertical>
        <SectionHeader />
        <SectionTitle title={title} subtitle={subtitle} />
        <Container>
          {content}
        </Container>
        { app.cart.subtotal &&
        <Menu borderless fixed={'bottom'} size='massive' widths={1} style={{ height: '7.5em' }} >
          <Container>
            <Menu.Item>
              <Container>
                <Header as='h3' style={{ fontSize: '1.5em' }}>
                  ${parseFloat(app.cart.subtotal).toFixed(2)}
                </Header>
                <Button as='button' button={{ type: 'submit' }} color='teal' loading={app.loading} size='big'>
                  <Button.Content visible style={{ width: '200px' }}>
                    Submit Payment
                  </Button.Content>
                </Button>
              </Container>
            </Menu.Item>
          </Container>
        </Menu>
        }
      </Segment>
    </Form>
    <SectionFooter />
  </>)
}

export default CheckoutPage
